import { iconPause, iconPlay } from "@kensho/icons";
import { Button, HeadlessButton, Tooltip } from "@kensho/neo";
import clsx from "clsx";
import { throttle } from "lodash-es";
import { use, useMemo, useState } from "react";

import IconSkipAhead from "../../assets/skip-ahead.svg";
import IconSkipBack from "../../assets/skip-back.svg";
import { TranscriptionContext } from "../../providers/transcription-context";
import { APITranscript } from "../../types/types";
import { PlaybackRateButton } from "./actions/playback-rate-button";
import { VolumeButton } from "./actions/volume-button";
import { MediaContainer } from "./media-container";
import { Timeline } from "./timeline";

interface ActionBarProps {
  transcript: APITranscript;
  mediaFile?: File;
  mediaPlayable: boolean;
  currentTime: number;
  seekMedia: (options: {
    timeSeconds: number;
    play?: boolean;
    scroll?: boolean;
  }) => void;
  paused: boolean;
  setPaused: React.Dispatch<React.SetStateAction<boolean>>;
  duration?: number;
  setDuration: React.Dispatch<React.SetStateAction<number | undefined>>;
  playbackRate: number;
  setPlaybackRate: React.Dispatch<React.SetStateAction<number>>;
  setCurrentTime: React.Dispatch<React.SetStateAction<number>>;
  onMediaError: () => void;
  setMediaEle: React.Dispatch<React.SetStateAction<HTMLMediaElement | null>>;
  openFileChooser: () => void;
}

export function ActionBar(props: ActionBarProps) {
  const {
    transcript,
    mediaFile,
    mediaPlayable,
    currentTime,
    seekMedia,
    paused,
    setPaused,
    duration,
    setDuration,
    playbackRate,
    setPlaybackRate,
    setCurrentTime,
    onMediaError,
    setMediaEle,
    openFileChooser,
  } = props;

  const [volume, setVolume] = useState<number>(0.5);
  const [savedVol, setSavedVol] = useState<number>(0.5);

  const { stage } = use(TranscriptionContext);

  const onTimeUpdate = useMemo(
    () =>
      throttle(
        (event: React.SyntheticEvent<HTMLMediaElement, Event>) =>
          setCurrentTime((event.target as HTMLMediaElement).currentTime),
        100,
      ),
    [setCurrentTime],
  );

  return (
    <Tooltip
      content="The audio file expired. Click to re-upload."
      disabled={!!mediaPlayable && !!mediaFile}
    >
      <HeadlessButton
        onClick={!mediaPlayable || !mediaFile ? openFileChooser : undefined}
        type="button"
        aria-label="Upload audio file"
        className={clsx(
          "w-full",
          !mediaPlayable || !mediaFile ? "bg-[#0000000A]" : "hover:cursor-auto",
        )}
      >
        <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between gap-x-1">
          <div className="ml-10 flex items-center justify-between gap-x-1">
            {mediaFile && (
              <MediaContainer
                setMediaEle={setMediaEle}
                mediaPlayable={mediaPlayable}
                mediaFile={mediaFile}
                muted={volume === 0}
                paused={paused}
                setPaused={setPaused}
                playbackRate={playbackRate}
                onMediaError={onMediaError}
                onLoadedMetadata={(event) => {
                  const mediaEleDuration = event.currentTarget.duration;
                  // wait for true duration to be reported before updating react state
                  if (
                    mediaEleDuration !== Infinity &&
                    mediaEleDuration !== Number.MAX_SAFE_INTEGER
                  ) {
                    setDuration(event.currentTarget.duration || 0);
                  }

                  // if we're in POST_TRANSCRIPTION noop, user switched the media
                  if (stage === "POST_TRANSCRIPTION") return;
                }}
                onDurationChange={(event) => {
                  const mediaEleDuration = event.currentTarget.duration;
                  // wait for true duration to be reported before updating react state
                  if (
                    mediaEleDuration === Infinity ||
                    mediaEleDuration === Number.MAX_SAFE_INTEGER
                  )
                    return;
                  setDuration(mediaEleDuration);
                }}
                onPlay={(event) => {
                  if (!event.currentTarget.seeking) setPaused(false);
                }}
                onPause={(event) => {
                  if (!event.currentTarget.seeking) setPaused(true);
                }}
                onTimeUpdate={onTimeUpdate}
                volume={volume}
              />
            )}
            {["POST_TRANSCRIPTION"].includes(stage) && (
              <>
                <Button
                  disabled={!mediaFile}
                  onClick={() => seekMedia({ timeSeconds: currentTime - 3 })}
                  minimal
                  intent="primary"
                >
                  <img
                    src={IconSkipBack}
                    alt="Skip Back 3 Seconds"
                    className={clsx(
                      "text-brand-700",
                      (!mediaPlayable || !mediaFile) && "opacity-50",
                    )}
                  />
                </Button>

                <Button
                  disabled={!mediaPlayable || !mediaFile}
                  onClick={() => setPaused((prev) => !prev)}
                  icon={paused ? iconPlay : iconPause}
                  intent="primary"
                  rounded
                  size="small"
                  aria-label={paused ? "Play" : "Pause"}
                />

                <Button
                  disabled={!mediaFile}
                  onClick={() => seekMedia({ timeSeconds: currentTime + 3 })}
                  minimal
                  intent="primary"
                >
                  <img
                    src={IconSkipAhead}
                    alt="Skip Ahead 3 Seconds"
                    className={clsx(
                      "text-brand-700",
                      (!mediaPlayable || !mediaFile) && "opacity-50",
                    )}
                  />
                </Button>
              </>
            )}
          </div>
          <div className="grow">
            <Timeline
              hasMedia={Boolean(mediaFile)}
              transcript={transcript}
              duration={duration}
              currentTime={currentTime}
              onSeek={(time) => seekMedia({ timeSeconds: time, play: false })}
            />
          </div>
          <div className="mr-10 flex items-center gap-2">
            <PlaybackRateButton
              disabled={!mediaPlayable || !mediaFile}
              playbackRate={playbackRate}
              onChange={(nextPlaybackRate) => setPlaybackRate(nextPlaybackRate)}
            />
            <VolumeButton
              disabled={!mediaPlayable || !mediaFile}
              volume={volume}
              onClick={() => {
                if (volume === 0) {
                  if (savedVol === 0) {
                    setVolume(0.5);
                  } else {
                    setVolume(savedVol);
                    setSavedVol(0);
                  }
                } else {
                  setSavedVol(volume);
                  setVolume(0);
                }
              }}
              onChange={(nextVolume) => {
                setVolume(nextVolume);
              }}
            />
          </div>
        </div>
      </HeadlessButton>
    </Tooltip>
  );
}
